/**
 * KK Cart — shared cart actions for every KK component.
 *
 * Load once per component that needs it (the browser runs a module URL only once):
 *   {% render 'kk-commerce-scripts' %}
 *
 * Declarative (event delegation — works for markup added later too):
 *   <button data-kk-add-to-cart data-variant-id="123" data-quantity="1">          add one variant
 *   <button data-kk-add-to-cart data-items='[{"id":123,"quantity":1}, …]'>         add several at once
 *   <button data-kk-cart-change data-line-key="…" data-quantity="0">               set a line's quantity
 *   <a href="/cart" data-kk-cart-open>                                             open the cart drawer
 *                                                                                  (follows the link if there's no drawer)
 *   <span data-kk-cart-count>                                                      live cart quantity
 *   <p data-kk-cart-error role="alert" hidden>                                      shows cart errors
 *   add `data-kk-no-open` to an add button to stay where you are after adding.
 *   Buttons get data-state="loading" | "added" | "error" while working.
 *
 * Re-rendering: every element with data-kk-cart-section="<section id>" is replaced with that section's
 * fresh HTML after each change (Shopify Section Rendering API, max 5 sections per request).
 *
 * Events on document:
 *   kk:cart:updated  { cart }      after every successful change
 *   kk:cart:error    { message }   when Shopify rejects a change
 *   kk:cart:open                   ask the cart drawer to open
 * Shopify's CartLinesUpdateEvent is dispatched too, so Horizon's own cart parts stay in sync.
 *
 * JS API: window.KKCart.add(items, options), .change(lineKey, quantity), .get(), .open()
 */
// Shopify's standard events file is an optional extra (it keeps Horizon's own cart parts in sync).
// It is downloaded from cdn.shopify.com; if it is blocked, slow or down, everything here still works.
let standardEvents = null;
try {
  standardEvents = await Promise.race([
    import('@shopify/events'),
    new Promise((_, reject) => setTimeout(() => reject(new Error('timed out')), 3000)),
  ]);
} catch (error) {
  console.warn('[KK] Shopify standard events unavailable; cart works without syncing Horizon parts.', error);
}
const { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } = standardEvents ?? {};
const CART_LINES_UPDATE = StandardEvents?.cartLinesUpdate ?? 'shopify:cart:lines-update';

/** The promise Horizon's listeners wait on; a plain stand-in when the events file is missing. */
function createDeferred() {
  if (CartLinesUpdateEvent?.createPromise) return CartLinesUpdateEvent.createPromise();
  let resolve;
  let reject;
  const promise = new Promise((a, b) => {
    resolve = a;
    reject = b;
  });
  promise.catch(() => {});
  return { promise, resolve, reject };
}

function toCart(ajaxCart) {
  return CartLinesUpdateEvent?.createCartFromAjaxResponse
    ? CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart)
    : { totalQuantity: ajaxCart.item_count };
}

function dispatchLinesUpdate(payload) {
  if (CartLinesUpdateEvent) document.dispatchEvent(new CartLinesUpdateEvent(payload));
}

const ROOT = window.Shopify?.routes?.root || '/';
const ROUTES = {
  add: `${ROOT}cart/add.js`,
  change: `${ROOT}cart/change.js`,
  cart: `${ROOT}cart.js`,
};
const MAX_SECTIONS = 5;
const STATE_RESET_MS = 2000;

class KKCartController {
  /** Requests run one after another so quick clicks can't overwrite each other. */
  #queue = Promise.resolve();
  #lastCount = null;
  #sadTimer = 0;

  constructor() {
    document.addEventListener('click', (event) => this.#onClick(event));

    // Any cart change on the page (KK or Horizon's own product form) updates the count badges.
    document.addEventListener(CART_LINES_UPDATE, (event) => {
      event.promise
        ?.then((result) => {
          const count = result?.cart?.totalQuantity ?? result?.detail?.itemCount;
          if (typeof count === 'number') this.#setCount(count);
        })
        .catch(() => {});
    });
  }

  /**
   * Add one or more variants.
   * @param {{ id: number | string, quantity?: number, properties?: object }[]} items
   * @param {{ open?: boolean, trigger?: HTMLElement }} [options]
   */
  add(items, { open = true, trigger } = {}) {
    const lines = items
      .filter((item) => item && item.id)
      .map((item) => ({ id: Number(item.id), quantity: Number(item.quantity) || 1, properties: item.properties }));

    if (!lines.length) return Promise.reject(new Error('Nothing to add'));

    return this.#enqueue(async () => {
      const deferred = createDeferred();
      dispatchLinesUpdate({
          action: 'add',
          context: 'product',
          lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
          promise: deferred.promise,
      });

      try {
        this.#setBusy(true);
        const response = await this.#post(ROUTES.add, { items: lines });
        // /cart/add.js returns only the added items, so read the whole cart afterwards.
        const cart = await this.get();
        this.#finish(cart, response.sections, deferred, trigger);
        if (open) this.open();
        return cart;
      } catch (error) {
        this.#fail(error, deferred, trigger);
        throw error;
      } finally {
        this.#setBusy(false);
      }
    });
  }

  /**
   * Set the quantity of a cart line (0 removes it).
   * @param {string} lineKey - the line item `key`
   * @param {number} quantity
   * @param {{ trigger?: HTMLElement }} [options]
   */
  change(lineKey, quantity, { trigger } = {}) {
    const qty = Math.max(0, Number(quantity) || 0);

    return this.#enqueue(async () => {
      const deferred = createDeferred();
      dispatchLinesUpdate({
          action: qty > 0 ? 'update' : 'remove',
          context: 'cart',
          lines: [{ id: String(lineKey), quantity: qty }],
          promise: deferred.promise,
      });

      try {
        this.#setBusy(true);
        // /cart/change.js returns the whole cart.
        const cart = await this.#post(ROUTES.change, { id: lineKey, quantity: qty });
        this.#finish(cart, cart.sections, deferred, trigger);
        return cart;
      } catch (error) {
        this.#fail(error, deferred, trigger);
        throw error;
      } finally {
        this.#setBusy(false);
      }
    });
  }

  /** @returns {Promise<object>} the current cart (Ajax API shape) */
  async get() {
    const response = await fetch(ROUTES.cart, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Could not load the cart');
    return response.json();
  }

  open() {
    // Remember the request: if the drawer script is still loading, it opens as soon as it is ready.
    document.documentElement.dataset.kkCartOpenRequested = String(Date.now());
    const drawer = /** @type {any} */ (document.querySelector('kk-cart-drawer'));
    if (typeof drawer?.open === 'function') drawer.open();
    else document.dispatchEvent(new CustomEvent('kk:cart:open'));
  }

  // ---------------------------------------------------------------- internals

  #enqueue(task) {
    const run = this.#queue.then(task, task);
    this.#queue = run.catch(() => {});
    return run;
  }

  async #post(url, body) {
    const sections = this.#sectionIds();
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...body,
        ...(sections.length ? { sections, sections_url: window.location.pathname } : {}),
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    }
    return data;
  }

  #sectionIds() {
    const ids = [...document.querySelectorAll('[data-kk-cart-section]')].map((el) => el.dataset.kkCartSection);
    return [...new Set(ids)].filter(Boolean).slice(0, MAX_SECTIONS);
  }

  #finish(cart, sections, deferred, trigger) {
    if (sections) this.#renderSections(sections);

    deferred.resolve({
      cart: toCart(cart),
      detail: { items: cart.items, itemCount: cart.item_count, source: 'kk-cart' },
    });

    const previous = this.#lastCount ?? Number(document.querySelector('[data-kk-cart-initial-count]')?.dataset.kkCartInitialCount ?? cart.item_count);
    const decreased = cart.item_count < previous;
    this.#lastCount = cart.item_count;

    this.#setCount(cart.item_count);
    this.#setState(trigger, 'added');
    if (decreased) this.#mascotsSad();
    document.dispatchEvent(new CustomEvent('kk:cart:updated', { detail: { cart, previousCount: previous, decreased } }));
  }

  #fail(error, deferred, trigger) {
    const message = error?.message || 'Something went wrong. Please try again.';
    if (CartErrorEvent) document.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
    document.dispatchEvent(new CustomEvent('kk:cart:error', { detail: { message } }));
    this.#setState(trigger, 'error', message);
    this.#showError(message);

    // Let Horizon's listeners settle with the real cart.
    this.get()
      .then((cart) =>
        deferred.resolve({
          cart: toCart(cart),
          detail: { didError: true, items: cart.items, itemCount: cart.item_count, source: 'kk-cart' },
        })
      )
      .catch(() => deferred.reject(error));
  }

  /** Replace every [data-kk-cart-section] region with its fresh render, keeping focus where it was. */
  #renderSections(sections) {
    const focusId = document.activeElement?.closest('[data-kk-focus-id]')?.dataset.kkFocusId;

    for (const [id, html] of Object.entries(sections)) {
      if (!html) continue;
      const fresh = new DOMParser().parseFromString(html, 'text/html');
      const replacement = fresh.querySelector(`[data-kk-cart-section="${id}"]`);
      if (!replacement) continue;

      document.querySelectorAll(`[data-kk-cart-section="${id}"]`).forEach((region) => {
        region.replaceWith(document.importNode(replacement, true));
      });
    }

    if (focusId) {
      /** @type {HTMLElement | null} */ (document.querySelector(`[data-kk-focus-id="${CSS.escape(focusId)}"]`))?.focus();
    }
  }

  /** Marks every cart region busy while a change is in flight (CSS dims the lines — no system cursor). */
  #setBusy(busy) {
    document.querySelectorAll("[data-kk-cart-section]").forEach((region) => {
      if (busy) region.setAttribute("aria-busy", "true");
      else region.removeAttribute("aria-busy");
    });
  }

  /** Shows the message in every [data-kk-cart-error] alert line for a few seconds. */
  #showError(message) {
    document.querySelectorAll('[data-kk-cart-error]').forEach((line) => {
      line.textContent = message;
      line.hidden = false;
      clearTimeout(line._kkErrorTimer);
      line._kkErrorTimer = setTimeout(() => (line.hidden = true), 5000);
    });
  }

  /** Mascots look sad for a moment after something was removed, then go back to their normal mood. */
  #mascotsSad() {
    const mascots = document.querySelectorAll('[data-kk-mascot]');
    mascots.forEach((el) => {
      el.dataset.sad = '';
      el.dataset.mood = 'sad';
    });
    clearTimeout(this.#sadTimer);
    this.#sadTimer = setTimeout(() => {
      document.querySelectorAll('[data-kk-mascot]').forEach((el) => {
        delete el.dataset.sad;
        el.dataset.mood = el.dataset.baseMood || 'run';
      });
    }, 2800);
  }

  /** Updates every [data-kk-cart-count] (header badge etc.). */
  #setCount(count) {
    const value = String(count);
    document.querySelectorAll('[data-kk-cart-count]').forEach((el) => {
      el.textContent = value;
      el.dataset.count = value;
    });
  }

  #setState(trigger, state, message = '') {
    if (!trigger || !trigger.isConnected) return;
    trigger.dataset.state = state;
    trigger.removeAttribute('aria-busy');
    if (message) trigger.setAttribute('title', message);

    clearTimeout(trigger._kkStateTimer);
    trigger._kkStateTimer = setTimeout(() => {
      delete trigger.dataset.state;
      trigger.removeAttribute('title');
    }, STATE_RESET_MS);
  }

  #onClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!(target instanceof Element)) return;

    const openButton = target.closest('[data-kk-cart-open]');
    if (openButton) {
      // Without the KK cart drawer on the page, a link simply goes to /cart.
      if (!document.querySelector('kk-cart-drawer')) return;
      event.preventDefault();
      this.open();
      return;
    }

    const addButton = /** @type {HTMLButtonElement | null} */ (target.closest('[data-kk-add-to-cart]'));
    if (addButton) {
      event.preventDefault();
      if (addButton.disabled || addButton.dataset.state === 'loading') return;

      let items = [];
      try {
        items = addButton.dataset.items
          ? JSON.parse(addButton.dataset.items)
          : [{ id: addButton.dataset.variantId, quantity: addButton.dataset.quantity || 1 }];
      } catch {
        return;
      }

      addButton.dataset.state = 'loading';
      addButton.setAttribute('aria-busy', 'true');
      this.add(items, { open: !addButton.hasAttribute('data-kk-no-open'), trigger: addButton }).catch(() => {});
      return;
    }

    const changeButton = /** @type {HTMLButtonElement | null} */ (target.closest('[data-kk-cart-change]'));
    if (changeButton) {
      event.preventDefault();
      if (changeButton.disabled || changeButton.dataset.state === 'loading') return;

      changeButton.dataset.state = 'loading';
      changeButton.setAttribute('aria-busy', 'true');
      this.change(changeButton.dataset.lineKey, Number(changeButton.dataset.quantity), { trigger: changeButton }).catch(
        () => {}
      );
    }
  }
}

if (!window.KKCart) {
  window.KKCart = new KKCartController();
}
